/**
 * @file api.ts
 * @brief TanStack Query hooks for Medical Assessment + Case Hub.
 *
 * @author DES
 * @date 02-09-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@/shared/api/client";
import { medicalAssessmentEndpoints } from "@/shared/api/endpoints";
import {
  AddStatementResponseSchema,
  AssessmentDetailResponseSchema,
  AssessmentsListResponseSchema,
  CaseHubPullResponseSchema,
  CreateAssessmentResponseSchema,
  AutoGenerateResponseSchema,
  ExaminationResponseSchema,
  MlrSupplementsResponseSchema,
  PrognosesListResponseSchema,
  PrognosisConfirmResponseSchema,
  DoctorLinkResponseSchema,
  DoctorLinkLookupResponseSchema,
  IntakeInboxResponseSchema,
  CoverageResponseSchema,
  ReportLifecycleResponseSchema,
  type MlrClaimantWarning,
  type MlrCoverage,
  ReportSectionsResponseSchema,
  type MlrReportLifecycle,
  MlrInstructionsResponseSchema,
  MlrInstructionsSaveResponseSchema,
  AcceptIntakeResponseSchema,
  type CaseHub,
  type DoctorLink,
  type MedicalAssessment,
  type MlrReportSections,
  type MlrRecordsReview,
  type MlrRule,
  type MlrRulebookPayload,
  MlrRulebookResponseSchema,
  MlrRulebookPreviewSchema,
} from "./schemas";

export const medicalAssessmentKeys = {
  all: ["company", "medical-assessment"] as const,
  list: () => ["company", "medical-assessment", "list"] as const,
  inbox: () => ["company", "medical-assessment", "inbox"] as const,
  detail: (id: string) => ["company", "medical-assessment", "detail", id] as const,
  instructions: () => ["company", "medical-assessment", "instructions"] as const,
  rulebook: () => ["company", "medical-assessment", "rulebook"] as const,
  doctorLink: () => ["company", "medical-assessment", "doctor-link"] as const,
};

export function useMedicalAssessmentsList() {
  return useQuery<MedicalAssessment[]>({
    queryKey: medicalAssessmentKeys.list(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(medicalAssessmentEndpoints.list);
      return AssessmentsListResponseSchema.parse(raw).assessments;
    },
  });
}

/**
 * Per-section report instructions plus every published version. Platform-wide
 * and super-admin only — not tied to an assessment or a tenant. (14-09-2026)
 */
export function useMlrInstructions() {
  return useQuery({
    queryKey: medicalAssessmentKeys.instructions(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(medicalAssessmentEndpoints.instructions);
      return MlrInstructionsResponseSchema.parse(raw);
    },
  });
}

export function useSaveMlrInstructions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      section: string;
      body: string;
      note?: string;
    }) => {
      const raw = await apiPut<unknown>(
        medicalAssessmentEndpoints.instructions,
        input,
      );
      return MlrInstructionsSaveResponseSchema.parse(raw).instructions;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: medicalAssessmentKeys.instructions(),
      });
    },
  });
}

// ----------------------------------------------------------------------
// The rulebook — what decides what goes in a report (Phase E)
// ----------------------------------------------------------------------

export function useMlrRulebook() {
  return useQuery<MlrRulebookPayload>({
    queryKey: medicalAssessmentKeys.rulebook(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(medicalAssessmentEndpoints.rulebook);
      return MlrRulebookResponseSchema.parse(raw);
    },
  });
}

/**
 * ⚠️ Read-only, and it must stay that way.
 *
 * A wrong rule is wrong on every report from the moment it is published,
 * quietly, in a field that decides a tariff. This runs a draft against real
 * past cases and returns the diff — nothing is written.
 */
export function usePreviewRulebook() {
  return useMutation({
    mutationFn: async (input: { rules: MlrRule[]; sample?: number }) => {
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.rulebookPreview,
        input,
      );
      return MlrRulebookPreviewSchema.parse(raw).preview;
    },
  });
}

export function usePublishRulebook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { rules: MlrRule[]; note?: string }) => {
      const raw = await apiPut<unknown>(
        medicalAssessmentEndpoints.rulebook,
        input,
      );
      return raw;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: medicalAssessmentKeys.rulebook() });
    },
  });
}

export function useMedicalIntakeInbox() {
  return useQuery<MedicalAssessment[]>({
    queryKey: medicalAssessmentKeys.inbox(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(medicalAssessmentEndpoints.inbox);
      return IntakeInboxResponseSchema.parse(raw).inbox;
    },
  });
}

export function useAcceptMedicalIntake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assessmentId: string) => {
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.acceptIntake(assessmentId),
        {},
      );
      return AcceptIntakeResponseSchema.parse(raw).assessment;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: medicalAssessmentKeys.all });
    },
  });
}

export function useMedicalAssessmentDetail(id: string | undefined) {
  return useQuery({
    queryKey: medicalAssessmentKeys.detail(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(medicalAssessmentEndpoints.byId(id!));
      return AssessmentDetailResponseSchema.parse(raw);
    },
  });
}

export function useCreateMedicalAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title?: string; caseHub?: CaseHub }) => {
      const raw = await apiPost<unknown>(medicalAssessmentEndpoints.create, input);
      return CreateAssessmentResponseSchema.parse(raw).assessment;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: medicalAssessmentKeys.all });
    },
  });
}

export function useSeedAleyadaDemoAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Explicit, not defaulted: the endpoint now seeds every §8 prognosis as
      // an unconfirmed draft unless told otherwise, which is the right state
      // for testing attestation on mobile but empties §8.2 on export — and
      // export is what this page is for. (13-09-2026)
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.demoSeedAleyada,
        { prognosisMode: "suggest-first" },
      );
      return CreateAssessmentResponseSchema.parse(raw).assessment;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: medicalAssessmentKeys.all });
    },
  });
}

export function useDeleteMedicalAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete<unknown>(medicalAssessmentEndpoints.byId(id));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: medicalAssessmentKeys.all });
    },
  });
}

export function useAutoGenerateReport(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input?: { mode?: "fixture" | "llm" }) => {
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.autoGenerate(assessmentId),
        input ?? {},
      );
      return AutoGenerateResponseSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: medicalAssessmentKeys.detail(assessmentId),
      });
    },
  });
}

export function usePatchCaseHubIdentity(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      claimantDisplayName?: string;
      uniqueReferenceNumber?: string;
      identity?: Record<string, string>;
      previousClaimantName?: string;
    }) => {
      const raw = await apiPatch<unknown>(
        medicalAssessmentEndpoints.caseHubIdentity(assessmentId),
        input,
      );
      return CaseHubPullResponseSchema.parse(raw).assessment;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: medicalAssessmentKeys.detail(assessmentId),
      });
      void qc.invalidateQueries({ queryKey: medicalAssessmentKeys.all });
    },
  });
}

export function useDoctorLink() {
  return useQuery<DoctorLink | null>({
    queryKey: medicalAssessmentKeys.doctorLink(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(medicalAssessmentEndpoints.doctorLink);
      return DoctorLinkResponseSchema.parse(raw).doctorLink;
    },
  });
}

export function useUpdateDoctorLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { doctorId?: number | null; doctorKey?: string | null }) => {
      const raw = await apiPut<unknown>(
        medicalAssessmentEndpoints.doctorLink,
        input,
      );
      return DoctorLinkResponseSchema.parse(raw).doctorLink;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: medicalAssessmentKeys.doctorLink() });
    },
  });
}

export function useLookupDoctorLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input?: {
      email?: string;
      databaseName?: string;
      mode?: "fixture" | "live";
      autoLink?: boolean;
    }) => {
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.doctorLinkLookup,
        input ?? {},
      );
      return DoctorLinkLookupResponseSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: medicalAssessmentKeys.doctorLink() });
    },
  });
}

export async function downloadGripXml(assessmentId: string): Promise<void> {
  const { default: axios } = await import("axios");
  const { tokenStorage } = await import("@/shared/api/tokenStorage");
  const token = tokenStorage.get();
  const response = await axios.get(
    medicalAssessmentEndpoints.exportGripXml(assessmentId),
    {
      responseType: "blob",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  const blob = new Blob([response.data], { type: "application/xml" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `grip-${assessmentId}.xml`;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * @param allowUnsigned export before the Statement of Truth is signed — for
 *   reading the layout on paper before committing to it. The PDF comes back
 *   stamped *DRAFT — NOT FOR SERVICE* on every page and named `…-DRAFT.pdf`,
 *   so it cannot be mistaken for the report.
 */
export async function downloadMlrPdf(
  assessmentId: string,
  allowUnsigned = false,
): Promise<string[]> {
  const { default: axios } = await import("axios");
  const { tokenStorage } = await import("@/shared/api/tokenStorage");
  const token = tokenStorage.get();
  const response = await axios.get(
    medicalAssessmentEndpoints.exportMlrPdf(assessmentId),
    {
      responseType: "blob",
      params: allowUnsigned ? { allowUnsigned: "true" } : undefined,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  const disposition = response.headers["content-disposition"] as string | undefined;
  const filenameMatch = disposition?.match(/filename="([^"]+)"/);
  const filename = filenameMatch?.[1] ?? `MLR-${assessmentId}.pdf`;
  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);

  const warningsHeader = response.headers["x-mlr-warnings"] as string | undefined;
  if (!warningsHeader) return [];
  try {
    const parsed: unknown = JSON.parse(warningsHeader);
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : [];
  } catch {
    return [];
  }
}

export type UpdateStatementInput = {
  details: string;
  facts?: Record<string, unknown>;
};

export function useUpdateAssessmentStatement(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateStatementInput & { statementId: string }) => {
      const { statementId, ...body } = input;
      const raw = await apiPut<unknown>(
        medicalAssessmentEndpoints.statement(assessmentId, statementId),
        body,
      );
      return AddStatementResponseSchema.parse(raw).statement;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: medicalAssessmentKeys.detail(assessmentId),
      });
      void qc.invalidateQueries({ queryKey: medicalAssessmentKeys.all });
    },
  });
}

/**
 * The assembled 13-section report, straight from the model the PDF is drawn
 * from. What belongs in a section is decided once, server-side; the panel
 * renders it. (13-09-2026)
 */
export function useReportSections(assessmentId: string | undefined) {
  return useQuery<MlrReportSections>({
    queryKey: [
      ...medicalAssessmentKeys.detail(assessmentId ?? ""),
      "report-sections",
    ],
    enabled: Boolean(assessmentId),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        medicalAssessmentEndpoints.reportSections(assessmentId!),
      );
      return ReportSectionsResponseSchema.parse(raw) as MlrReportSections;
    },
  });
}

/**
 * The two answers to a finding the recording does not support.
 *
 * Both invalidate the whole assessment detail, not just the section model: the
 * statement list, the coverage counts and the PDF all change with it, and this
 * page caches the meeting under more than one key (see
 * `webpanel-meeting-detail-two-query-keys` for the shape of that mistake).
 */
/**
 * §6 — save the doctor's answer about which records they reviewed.
 *
 * ⚠️ Saving IS the confirm: send `confirmed: true` and §6 starts printing it.
 * Before that the report says nothing, which is the point — it used to print a
 * fixed list of five record types from a single Case Hub boolean.
 */
export function useSaveRecordsReview(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (review: MlrRecordsReview) => {
      await apiPut<unknown>(
        medicalAssessmentEndpoints.recordsReview(assessmentId),
        review,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: medicalAssessmentKeys.detail(assessmentId),
      });
    },
  });
}

/**
 * The doctor answers the one decision the rulebook could not settle.
 *
 * This is the doctor STATING the classification, not the rulebook deriving it —
 * so no citation is attached, and the report shows none.
 */
export function useAnswerPrognosisDecision(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      prognosisId: string;
      decision: string;
      value: string | number;
    }) => {
      await apiPut<unknown>(
        medicalAssessmentEndpoints.prognosisDecision(
          assessmentId,
          input.prognosisId,
        ),
        { decision: input.decision, value: input.value },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: medicalAssessmentKeys.detail(assessmentId),
      });
    },
  });
}

export function useVerifyStatement(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (statementId: string) => {
      await apiPost<unknown>(
        medicalAssessmentEndpoints.verifyStatement(assessmentId, statementId),
        {},
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: medicalAssessmentKeys.detail(assessmentId),
      });
    },
  });
}

export function useDeleteStatement(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (statementId: string) => {
      await apiDelete<unknown>(
        medicalAssessmentEndpoints.statement(assessmentId, statementId),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: medicalAssessmentKeys.detail(assessmentId),
      });
    },
  });
}

/**
 * The consultation summary, and the words it must be shown under.
 *
 * ⚠️ The label is served, not typed here. It is part of the deliverable: a
 * record of what was said, displayed under a heading like "Doctor's findings",
 * is a clinical claim whatever the data is — and two clients plus a partner
 * each writing their own heading is three chances to get that wrong.
 */
export type MlrSummaryItem = {
  text: string;
  speaker: "doctor" | "claimant";
  quote: string;
  /** `followUps` only — whoever must do it, when that was said. */
  owner?: "doctor" | "claimant" | null;
};

/**
 * Meeting Intelligence's sections, under Meeting Intelligence's names.
 *
 * `overview` ↔ `meeting_overview`, `narrative` ↔ `meeting_summary`,
 * `keyPoints` ↔ `key_points`, `agreed` ↔ `key_decisions`, `followUps` ↔ `todos`,
 * `attention` ↔ `attention_risks`.
 */
export type MlrSessionSummary = {
  version: string;
  overview: string;
  /** The readable layer — written from the grounded items, never the transcript. */
  narrative?: string[];
  keyPoints: MlrSummaryItem[];
  agreed?: MlrSummaryItem[];
  followUps?: MlrSummaryItem[];
  attention?: MlrSummaryItem[];
  languages?: Array<{ name: string; share: number | null }>;
  clarity?: number | null;
  generatedAt?: string | null;
};

export function useSessionSummary(assessmentId: string | undefined) {
  return useQuery({
    queryKey: [
      ...medicalAssessmentKeys.detail(assessmentId ?? ""),
      "session-summary",
    ],
    enabled: Boolean(assessmentId),
    queryFn: async () => {
      const raw = await apiGet<{
        summary?: MlrSessionSummary | null;
        label?: { title: string; disclaimer: string };
      }>(medicalAssessmentEndpoints.sessionSummary(assessmentId!));

      return {
        // Null on most cases, and that is the normal state rather than a gap.
        summary: raw?.summary ?? null,
        label: raw?.label ?? { title: "", disclaimer: "" },
      };
    },
  });
}

export function useExamination(assessmentId: string | undefined) {
  return useQuery({
    queryKey: [...medicalAssessmentKeys.detail(assessmentId ?? ""), "examination"],
    enabled: Boolean(assessmentId),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        medicalAssessmentEndpoints.examination(assessmentId!),
      );
      return ExaminationResponseSchema.parse(raw).examination;
    },
  });
}

export function useUpsertExamination(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      generalFindings?: string;
      presentationFindings?: string;
      findingsByInjury?: Record<string, string>;
    }) => {
      const raw = await apiPut<unknown>(
        medicalAssessmentEndpoints.examination(assessmentId),
        input,
      );
      return ExaminationResponseSchema.parse(raw).examination;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: medicalAssessmentKeys.detail(assessmentId),
      });
      void qc.invalidateQueries({
        queryKey: [...medicalAssessmentKeys.detail(assessmentId), "examination"],
      });
    },
  });
}

export function useMlrSupplements(assessmentId: string | undefined) {
  return useQuery({
    queryKey: [...medicalAssessmentKeys.detail(assessmentId ?? ""), "mlr-supplements"],
    enabled: Boolean(assessmentId),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        medicalAssessmentEndpoints.mlrSupplements(assessmentId!),
      );
      return MlrSupplementsResponseSchema.parse(raw).supplements;
    },
  });
}

export function useUpsertMlrSupplements(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      medicalRecordsReview?: string;
      medicalRecordsEntries?: Array<{
        phase: "pre" | "post";
        source: string;
        dateLabel?: string;
        narrative: string;
        footnote?: string;
      }>;
      medicalRecordsSummary?: string;
      prognosisOpinion?: string;
      registrationDetails?: {
        gmcNumber?: string;
        mpsNumber?: string;
        icoReference?: string;
        medcoId?: string;
        medcoTrainingDate?: string;
        currentPositions?: string;
        experience?: string;
      };
      caseClassification?: {
        motorVehicleOccupant?: string;
        softTissueInjuryClaim?: string;
        firstReportInRelationToIncident?: string;
      };
      specialConsiderations?: string;
      futureTreatment?: string;
      qualifications?: string;
      /* §4 closing — the physician's view of the claimant's account. */
      accountAgreement?: "agrees" | "partly" | "disagrees" | null;
      accountAssessment?: string;
      injuriesDenied?: Array<{
        injury: string;
        source?: "clinic_list" | "instruction";
        note?: string | null;
      }>;
      exceptionalCircumstances?: string;
      /** §9's three Yes/No answers; omit to leave unchanged. */
      exceptionalWhiplashReported?: string | null;
      exceptionalCircumstancesReported?: string | null;
      exceptionalSeverityAgreed?: string | null;
      photoIdMatched?: boolean | null;
    }) => {
      const raw = await apiPut<unknown>(
        medicalAssessmentEndpoints.mlrSupplements(assessmentId),
        input,
      );
      return MlrSupplementsResponseSchema.parse(raw).supplements;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...medicalAssessmentKeys.detail(assessmentId), "mlr-supplements"],
      });
    },
  });
}

export function useSignMlrStatement(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      signerName: string;
      attestationAccepted: boolean;
    }) => {
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.mlrSignStatement(assessmentId),
        input,
      );
      return MlrSupplementsResponseSchema.parse(raw).supplements;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...medicalAssessmentKeys.detail(assessmentId), "mlr-supplements"],
      });
    },
  });
}

/**
 * What the consultation did not cover. Read alongside the report, not instead
 * of it: the checklist says what was never asked, which is invisible in the
 * report itself.
 */
export function useCoverage(assessmentId: string | undefined) {
  return useQuery<{ coverage: MlrCoverage; warnings: MlrClaimantWarning[] }>({
    queryKey: [...medicalAssessmentKeys.detail(assessmentId ?? ""), "coverage"],
    enabled: Boolean(assessmentId),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        medicalAssessmentEndpoints.coverage(assessmentId!),
      );
      const parsed = CoverageResponseSchema.parse(raw);
      /* Warnings ride with coverage because they are read at the same moment:
         before signing, on the same screen. */
      return { coverage: parsed.coverage, warnings: parsed.warnings ?? [] };
    },
  });
}

/**
 * A change to a signed report is either a correction or an amendment, and the
 * product asks rather than infers — see the plan's Decisions §2. Every write
 * to a signed report is refused with 409 `MLR_REPORT_SIGNED`; these two are
 * the ways forward.
 */
export function useReportLifecycle(assessmentId: string | undefined) {
  return useQuery<MlrReportLifecycle>({
    queryKey: [...medicalAssessmentKeys.detail(assessmentId ?? ""), "report-lifecycle"],
    enabled: Boolean(assessmentId),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        medicalAssessmentEndpoints.reportLifecycle(assessmentId!),
      );
      return ReportLifecycleResponseSchema.parse(raw).lifecycle;
    },
  });
}

/** Correction — the report is fixed before it goes out; the signature is withdrawn. */
export function useVoidReportSignature(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reason?: string } = {}) => {
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.reportVoidSignature(assessmentId),
        input,
      );
      return ReportLifecycleResponseSchema.parse(raw).lifecycle;
    },
    // Everything about the report changes shape here: it stops being frozen,
    // the sections come back live, and the edit targets reappear.
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: medicalAssessmentKeys.detail(assessmentId),
      });
    },
  });
}

/** Amendment — the signed report stands as issued; a new report supersedes it. */
export function useAmendReport(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title?: string; reason?: string } = {}) => {
      const raw = (await apiPost<unknown>(
        medicalAssessmentEndpoints.reportAmend(assessmentId),
        input,
      )) as { assessment?: { id?: string; title?: string } };
      const id = raw?.assessment?.id;
      if (!id) throw new Error("The amendment was not created");
      return { assessmentId: id, title: raw.assessment?.title ?? "" };
    },
    onSuccess: () => {
      // Both ends move: a new assessment exists, and the parent is now marked
      // superseded.
      void qc.invalidateQueries({ queryKey: medicalAssessmentKeys.all });
    },
  });
}

/**
 * ⚠️ No caller since 18-09-2026 — the §6 import panel was removed from the
 * report page. Kept because the backend routes are intact and records may yet
 * need attaching by hand; when they do it belongs at intake or on the Case
 * Hub, not on the page the doctor reads. Delete these three if that never
 * happens.
 */
export function useIngestMedicalRecordsText(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { text: string; append?: boolean }) => {
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.mlrIngestRecordsText(assessmentId),
        input,
      );
      return MlrSupplementsResponseSchema.parse(raw).supplements;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...medicalAssessmentKeys.detail(assessmentId), "mlr-supplements"],
      });
    },
  });
}

export function useIngestMedicalRecordsPdf(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; append?: boolean }) => {
      const formData = new FormData();
      formData.append("file", input.file);
      if (input.append === false) {
        formData.append("append", "false");
      }
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.mlrIngestRecordsPdf(assessmentId),
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      const parsed = MlrSupplementsResponseSchema.parse(raw);
      return {
        supplements: parsed.supplements,
        ingest: parsed.ingest,
      };
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...medicalAssessmentKeys.detail(assessmentId), "mlr-supplements"],
      });
    },
  });
}

export function useSeedMlrRecordsFromCaseHub(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input?: { overwrite?: boolean }) => {
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.mlrSeedRecordsFromCaseHub(assessmentId),
        input ?? {},
      );
      return MlrSupplementsResponseSchema.parse(raw).supplements;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...medicalAssessmentKeys.detail(assessmentId), "mlr-supplements"],
      });
    },
  });
}

/**
 * ⚠️ No caller since 18-09-2026 — §13 is seeded from MEDYX automatically, on a
 * Case Hub pull and on every generation, so the button that called this was
 * re-running something that had already happened. Kept alongside the records
 * ingest hooks above; delete all four if a manual path never returns.
 */
export function useSeedMlrRegistrationFromDoctor(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input?: {
      email?: string;
      databaseName?: string;
      mode?: "live" | "fixture";
      overwrite?: boolean;
    }) => {
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.mlrSeedRegistrationFromDoctor(assessmentId),
        input ?? {},
      );
      return MlrSupplementsResponseSchema.parse(raw).supplements;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...medicalAssessmentKeys.detail(assessmentId), "mlr-supplements"],
      });
    },
  });
}

export function usePrognoses(assessmentId: string | undefined) {
  return useQuery({
    queryKey: [...medicalAssessmentKeys.detail(assessmentId ?? ""), "prognoses"],
    enabled: Boolean(assessmentId),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        medicalAssessmentEndpoints.prognoses(assessmentId!),
      );
      return PrognosesListResponseSchema.parse(raw).prognoses;
    },
  });
}

export function useConfirmPrognosis(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      prognosisId: string;
      action: "agree" | "edit" | "voice";
      confirmedText?: string;
      attestationAccepted?: boolean;
      structuredFields?: Record<string, unknown>;
    }) => {
      const raw = await apiPost<unknown>(
        medicalAssessmentEndpoints.prognosisConfirm(
          assessmentId,
          input.prognosisId,
        ),
        {
          action: input.action,
          confirmedText: input.confirmedText,
          attestationAccepted: input.attestationAccepted,
          structuredFields: input.structuredFields,
        },
      );
      return PrognosisConfirmResponseSchema.parse(raw).prognosis;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...medicalAssessmentKeys.detail(assessmentId), "prognoses"],
      });
    },
  });
}
